import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import QRCode from 'qrcode.react'
import { isEqual as _isEqual } from 'lodash'

// @material-ui
import {
  Grid,
  Button,
  Collapse,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
} from '@material-ui/core'

// core
import {
  API_METHOD,
  SECURITY_CHECK_TYPE,
  SECURITY_CHECK_MODE,
  SECURITY_CHECK_MODE_NAME,
} from 'basic/config'
import { withLang } from '_core/hocs/withLang'
import { editMobile } from 'basic/forms/editMobile'
import { editPassword } from 'basic/forms/editPassword'
import { editFinPassword } from 'basic/forms/editFinPassword'
import { FormWithValidation } from '_core/components/FormWithValidation'
import { SecurityCheck } from '_core/components/SecurityCheck'
import { ModalOuter } from '_core/components/ModalOuter'
import { ModalInner } from '_core/components/ModalInner'
import { ClipboardInput } from '_core/components/ClipboardInput'


const FORM_PROPS = {
  [SECURITY_CHECK_MODE.SMS]: {
    decor: editMobile,
    method: API_METHOD.VISITOR_EDIT_SMSCHECK,
  },
  [SECURITY_CHECK_MODE.FINPASSWD]: {
    decor: editFinPassword,
    method: API_METHOD.VISITOR_EDIT_FINPASSWORD,
  },
}

const RawProfileSecurityFormView = props => {
  const {
    forms,
    login,
    checks,
    security,
    gauth,
    isModalOpen,
    onModalClose,
    setForms,
    canSubmit,
    onSubmitCheck,
    onCheckChange,
    getLangString: l,
  } = props

  const renderGauthCodeModal = () => {
    return (
      <ModalOuter open={isModalOpen} onClose={onModalClose} maxWidth="sm">
        <ModalInner
          title={l('r._.profile.security.label.gauth')}
          content={
            <>
              <ClipboardInput
                value={gauth.code}
                label={l('r._.profile.security.label.gauthkey')}
              />
              <QRCode value={gauth.qrcode.content} level="L" size={175} />
            </>
          }
        />
      </ModalOuter>
    )
  }

  const renderSubmitButton = type => {
    return (
      <SecurityCheck
        type={type}
        login={login}
        render={({ check, renderCheckForm }) => (
          <>
            <Button
              type="submit"
              disabled={!canSubmit(type)}
              onClick={() => onSubmitCheck(type, check)}
            >
              {l('r._.profile.security.action.save')}
            </Button>

            {renderCheckForm()}
          </>
        )}
      />
    )
  }

  const renderOptions = (type, title) => {
    const list = SECURITY_CHECK_MODE_NAME[type]

    return (
      <>
        <Typography variant="body1">{l(title)}</Typography>
        <RadioGroup name={type} value={checks[type]} onChange={onCheckChange}>
          {Object.keys(list).map(key => (
            <Fragment key={key}>
              <FormControlLabel
                value={key}
                disabled={security == null}
                checked={checks[type] === key}
                control={<Radio color="primary" />}
                label={l(list[key])}
              />
              {renderOptionForm(type, key)}
            </Fragment>
          ))}
        </RadioGroup>
      </>
    )
  }

  const renderOptionForm = (type, mode) => {
    if (forms[type][mode] == null) {
      return null
    }

    return (
      <Collapse in={forms[type][mode].isVisible}>
        <FormWithValidation
          submitText={l('r._.profile.security.action.save')}
          decor={FORM_PROPS[mode].decor}
          method={FORM_PROPS[mode].method}
          checkRequiredTouched={true}
          renderFormBody={({ outerProps, innerProps }) => {
            const data = {
              values: forms[type][mode].values,
              canSubmit: forms[type][mode].canSubmit,
            }

            const newData = {
              canSubmit: outerProps.canSubmit(innerProps),
              values: innerProps.values,
            }

            if (!_isEqual(data, newData)) {
              setForms(type, mode, newData)
            }

            return outerProps.renderFields(innerProps)
          }}
        />
      </Collapse>
    )
  }

  return (
    <div>
      <Grid container={true} justify="center">
        <Grid item={true} xs={12} sm={6} style={{ marginBottom: '20px' }}>
          {renderOptions(
            SECURITY_CHECK_TYPE.AUTH,
            'r._.profile.security.label.auth'
          )}

          {renderSubmitButton(SECURITY_CHECK_TYPE.AUTH)}
        </Grid>

        <Grid item={true} xs={12} sm={6} style={{ marginBottom: '20px' }}>
          {renderOptions(
            SECURITY_CHECK_TYPE.FINOP,
            'r._.profile.security.label.finance'
          )}

          {renderSubmitButton(SECURITY_CHECK_TYPE.FINOP)}
        </Grid>
      </Grid>

      <Grid container={true}>
        <Grid item={true} xs={12} sm={6} style={{ marginBottom: '20px' }}>
        <Typography variant="body1">{l('r._.profile.edit.password')}</Typography>
          <FormWithValidation
            decor={editPassword}
            checkRequiredTouched={true}
            submitText={l('r._.profile.security.action.save')}
            method={API_METHOD.VISITOR_EDIT_PASSWORD}
            renderFormBody={({ outerProps, innerProps }) => (
              <>
                {outerProps.renderFields(innerProps)}
                {outerProps.renderSuccess(innerProps)}
                {outerProps.renderFailure(innerProps)}
                {outerProps.renderProgress(innerProps)}
                {outerProps.renderResetButton(innerProps)}
                {outerProps.renderSubmitButton(innerProps)}
              </>
            )}
          />
        </Grid>
      </Grid>

      {gauth != null ? renderGauthCodeModal() : null}
    </div>
  )
}

RawProfileSecurityFormView.defaultProps = {}

RawProfileSecurityFormView.propTypes = {
  // parent props
  security: PropTypes.object,
  gauth: PropTypes.shape({
    code: PropTypes.string.isRequired,
    qrcode: PropTypes.shape({
      link: PropTypes.string,
      content: PropTypes.string,
    }).isRequired,
  }),
  forms: PropTypes.object.isRequired,
  login: PropTypes.string.isRequired,
  checks: PropTypes.object.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  onModalClose: PropTypes.func.isRequired,
  setForms: PropTypes.func.isRequired,
  canSubmit: PropTypes.func.isRequired,
  onSubmitCheck: PropTypes.func.isRequired,
  onCheckChange: PropTypes.func.isRequired,

  // `withLang` HOC props
  langInfo: PropTypes.object,
  langStrings: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired,
}

export const ProfileSecurityFormView = withLang(
  RawProfileSecurityFormView
)
